import type { AxiosInstance } from 'axios'
import type { IFactoryFunctionRequest, IOffice } from '@bc-library/interface'
import { EMethod } from '@bc-library/interface'
import { API_MAIN_VERSION, BASE_URL } from '@/config/constantes'
import useAxios from '@/services/api/useAxios'
import type { TOfficeResourceCreationFields, TResourceCreationFieldConfiguration } from '@/services/resource/creationFields'

const resource = 'offices'

const getAll = (axios: IFactoryFunctionRequest<Array<IOffice>, undefined>) => ({ firmId, isAdmin }: { firmId: string; isAdmin: boolean }) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/firm/${firmId}/${isAdmin}`,
  })
}

const getAllFilter = (axios: IFactoryFunctionRequest<Array<Pick<IOffice, '_id' | 'display'>>, undefined>) => ({ isAdmin }: { isAdmin: boolean }) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/firm/filter/${isAdmin}`,
  })
}

const filter = (axios: IFactoryFunctionRequest<Array<Pick<IOffice, '_id' | 'display'>>, undefined>) => ({
  page = 0,
  display = '',
  limit = 30,
  recruitmentScope = false,
  hrScope = false,
}: { page?: number; display?: string; limit?: number; recruitmentScope?: boolean; hrScope?: boolean }) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/settings/filter`,
    query: {
      page,
      display,
      limit,
      ...(recruitmentScope ? { recruitmentScope: 1 } : {}),
      ...(hrScope ? { hrScope: 1 } : {}),
    },
  })
}

const getById = (axios: IFactoryFunctionRequest<IOffice, undefined>) => (officeId: string) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${officeId}`,
  })
}

const update = (axios: IFactoryFunctionRequest<IOffice, IOffice>) => (office: IOffice) => {
  return axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/${office._id}`,
    data: office,
  })
}

const resourceCreationFields = (axios: IFactoryFunctionRequest<Array<TOfficeResourceCreationFields>, undefined>) => ({ countryId, search }: { countryId?: string, search?: string } = {}) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/resource-creation-fields`,
    query: { ...(countryId ? { countryId } : {}), ...(search ? { search } : {}) },
  })
}

// Fields of the creation form for the current user: a created resource joins the office of its creator
const currentResourceCreationFields = (axios: IFactoryFunctionRequest<TResourceCreationFieldConfiguration, undefined>) => () => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/resource-creation-fields/current`,
  })
}

const updateResourceCreationFields = (axios: IFactoryFunctionRequest<TOfficeResourceCreationFields, TResourceCreationFieldConfiguration>) => (officeId: string, configuration: TResourceCreationFieldConfiguration) => {
  return axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${officeId}/resource-creation-fields`,
    data: configuration,
  })
}

const superAdminList = (axios: IFactoryFunctionRequest<any, undefined>) => ({
  page = 0,
  limit = 30,
  search = '',
  officeId,
}: { page?: number; limit?: number; search?: string; officeId?: string }) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/superAdminList`,
    query: { page, limit, search, officeId },
  })
}

const superAdminCreate = (axios: IFactoryFunctionRequest<any, any>) => (data: any) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/superAdmin`,
    data,
  })
}

const superAdminUpdate = (axios: IFactoryFunctionRequest<any, any>) => (officeId: string, data: any) => {
  return axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/superAdmin/${officeId}`,
    data,
  })
}

export default (axios: AxiosInstance) => ({
  getAll: getAll(useAxios(axios)),
  getById: getById(useAxios(axios)),
  filter: filter(useAxios(axios)),
  update: update(useAxios(axios)),
  getAllFilter: getAllFilter(useAxios(axios)),
  superAdminList: superAdminList(useAxios(axios)),
  superAdminCreate: superAdminCreate(useAxios(axios)),
  superAdminUpdate: superAdminUpdate(useAxios(axios)),
  resourceCreationFields: resourceCreationFields(useAxios(axios)),
  currentResourceCreationFields: currentResourceCreationFields(useAxios(axios)),
  updateResourceCreationFields: updateResourceCreationFields(useAxios(axios)),
})
